import { Combobox, ErrorMessage, FormField, FormFieldLabel, Heading } from '@vwfs-bronson/bronson-react';
import { Contract } from 'cpfr-common';
import { FormikProps, useField, useFormikContext } from 'formik';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestInitialValues } from '../initialValues';
import { head } from 'lodash';
import { ComboboxOption } from 'types/bronson-react';

export type ContractSelectionProps = {
    contracts?: Contract[];
    currentContracts: Contract[];
    isSMECustomer?: boolean;
};

export const ContractSelection: React.FC<ContractSelectionProps> = ({ contracts, currentContracts, isSMECustomer }) => {
    const [, meta, helpers] = useField('contractsSelection');
    const [authorizationDebitField, , authorizationDebitHelpers] = useField('authorizationDebit');
    const { initialValues, setValues }: FormikProps<RequestInitialValues> = useFormikContext();
    const { t } = useTranslation('request');
    const [selectedItems, setSelectedItems] = useState<ComboboxOption[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setValueChange = useCallback((selectedContractsOptions: any): void => {
        setSelectedItems(selectedContractsOptions);
        if (!Array.isArray(contracts) || !contracts.length) {
            return;
        }
        if (!selectedContractsOptions.length) {
            setValues({
                ...initialValues,
                contractsSelection: [],
                authorizationDebit: {
                    ...initialValues.authorizationDebit,
                    authorizes: initialValues.authorizationDebit.authorizes,
                },
            });
        }
        let selectedContracts: Contract[] = [];
        for (const selected of selectedContractsOptions) {
            selectedContracts = [
                ...selectedContracts,
                ...contracts.filter((contract) => contract.contractNumber === selected.key),
            ];
        }

        // set authorizes as contract number when only one contract is selected
        if (selectedContracts && selectedContracts.length === 1) {
            const selectedContractNumber = head(selectedContracts)?.contractNumber;
            authorizationDebitHelpers.setValue({
                ...authorizationDebitField.value,
                authorizes: selectedContractNumber,
            });
        }

        helpers.setValue(selectedContracts || initialValues.contractsSelection);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!contracts) {
        return null;
    }

    const options: ComboboxOption[] = [
        ...contracts.filter((contract) => contract.isActive && contract.contractNumber && contract.productType),
        ...contracts.filter((contract) => !contract.isActive && contract.contractNumber && contract.productType),
    ]
        .map(({ carModel, productTypeFR, licensePlate, contractNumber, isActive }) => {
            const getProductTypeValue = (): string =>
                (isActive ? productTypeFR : t(`contract-selection.contract-expired`)) || '';
            const carInformation = `${carModel}, ${licensePlate}`;

            return {
                key: contractNumber,
                value: {
                    textValue: contractNumber,
                    content: (
                        <div>
                            <b>{contractNumber}</b>
                            <br aria-hidden="true" />
                            <b>{getProductTypeValue()}</b>
                            <br aria-hidden="true" />
                            {carModel && licensePlate && <span>{carInformation}</span>}
                        </div>
                    ),
                },
            };
        })
        .filter(Boolean);

    const defaultState = {
        value: currentContracts.map((contract) => ({
            value: contract.contractNumber,
            key: contract.contractNumber,
        })),
    };

    const label = `${t('contract-selection.label')}`;

    return (
        <>
            <FormFieldLabel htmlFor="contract-selection">
                <Heading level={2}>{label}</Heading>
            </FormFieldLabel>
            <FormField
                type="select"
                testId={'contract-selection'}
                id={'contract-selection'}
                errorMessage={
                    meta.touched &&
                    meta.error && <ErrorMessage testId={'textarea-error-message'}>{meta.error}</ErrorMessage>
                }
            >
                <Combobox
                    className="c-form-field--no-floating"
                    testId={'custom-contract-select'}
                    onChange={setValueChange}
                    value={selectedItems}
                    defaultState={defaultState}
                    multiple={isSMECustomer}
                    options={options}
                ></Combobox>
            </FormField>
        </>
    );
};
